import { onCleanup, Owner, runWithOwner } from "solid-js";

import Analytics, { StructuredEvents } from "@repo/utils/Analytics";
import UFunction from "@repo/utils/UFunction";
import UObject from "@repo/utils/UObject";
import UProxy from "@repo/utils/UProxy";
import UEventTarget from "@repo/utils-client/UEventTarget";
import UHTMLElement from "@repo/utils-client/UHTMLElement";

namespace SolidAnalytics {
  export function wrapClients(
    clients: Parameters<Analytics<any, any, any>["addClients"]>[0],
    getOwner: () => Owner
  ) {
    const res = { ...clients };
    for (const [key, client] of Object.entries(res)) {
      res[key] = () =>
        // @ts-expect-error
        Promise.resolve(client()).then((client) => {
          if (!client) return client;
          return UObject.transform(client, ([key, value]) => [
            key,
            value &&
              ((...args: any[]) =>
                runWithOwner(getOwner(), () =>
                  // @ts-expect-error
                  value(...args)
                )),
          ]);
        });
    }
    return res;
  }

  type TrackImpressionRef<E extends StructuredEvents.Schema> = {
    [K in keyof E]: (E[K] extends {
      readonly [StructuredEvents.actions]: {
        impression: infer ImpressionActionProperties extends
          Analytics.Properties;
      };
    }
      ? (
          ...[
            properties,
          ]: UObject.RequiredKeys<ImpressionActionProperties> extends never
            ? [(undefined | ImpressionActionProperties)?]
            : [ImpressionActionProperties]
        ) => (ref: HTMLElement) => void
      : {}) &
      TrackImpressionRef<Omit<E[K], symbol>>;
  };

  export const createTrackImpressionProxy = <E extends StructuredEvents.Schema>(
    analytics: Analytics<any, any, any>,
    events: E
  ) =>
    UProxy.createPathProxy((path) => (properties) => (ref: HTMLElement) => {
      const track = UFunction.debounce(
        UFunction.memo(() => {
          let current = events;
          analytics.track(
            // @ts-expect-error
            `${path.map((segment) => (current = current[segment])[StructuredEvents.formatted]).join(" - ")}: Impression`,
            { ...properties, viewportIntersection: true }
          );
        }),
        2000
      );
      onCleanup(() => track.clear());

      onCleanup(
        UHTMLElement.observe.intersection(ref, { threshold: 0.6 }, (entry) => {
          if (entry.isIntersecting) track();
          else track.clear();
        }).unobserve
      );

      onCleanup(
        UEventTarget.wrap(ref).on.capture("click", () => {
          track.immediate();
        }).off
      );
    }) as unknown as TrackImpressionRef<E>;
}

export default SolidAnalytics;
